/**
 * Buy Panel (formerly Share & Buy)
 * Renders the Buy Now state machine for a saved design
 *
 * H-165: the share section (platform tabs / AI copy generation / download)
 * was removed — the /api/share/* routes are decommissioned (H-079). Only the
 * Buy Now purchase flow remains, so the panel must never issue a
 * /api/share/* request.
 *
 * H-177 (bao 2026-10-16, D1=C): the post-publish social entry returns as a
 * "Make a poster" button in the success state — server poster via the LIVE
 * POST /api/customer/poster plus a PDP share link carrying ?share=<token>
 * (token endpoint = H-176, parallel). No /api/share/* route is touched.
 */

import { showToast } from '../../../utils/toast.js'
import { esc } from '../../../utils/escape.js'
import { openPosterModal } from '../../social/poster-modal.js'
import { fetchShareToken, buildShareUrl } from '../../../api/share-token.js'

let retryFn = null
let currentBuyNowProduct = null

export function openSharePanel(endProductData) {
  currentBuyNowProduct = endProductData

  const existing = document.getElementById('shareBuyPanel')
  if (existing) existing.remove()

  const overlay = document.createElement('div')
  overlay.id = 'shareBuyPanel'
  overlay.className = 'designer-share-overlay'
  overlay.innerHTML = `
    <div class="designer-share-modal">
      <div class="dshare-header">
        <h3>Buy</h3>
        <button class="dshare-close" id="sbClose">&times;</button>
      </div>
      <div class="dshare-buynow" id="sbBuyNow">
        <div class="dshare-buynow-loading">
          <div class="spinner"></div>
          <span>Thinking...</span>
        </div>
      </div>
    </div>
  `
  document.body.appendChild(overlay)

  document.getElementById('sbClose').onclick = closeSharePanel
  overlay.onclick = (e) => {
    if (e.target === overlay) closeSharePanel()
  }
}

export function updateBuyNowReady(shopifyProduct) {
  const el = document.getElementById('sbBuyNow')
  if (!el) return
  const url =
    shopifyProduct.shopify_product_url ||
    `https://mamamiya.store/products/${shopifyProduct.shopify_handle}`
  // Price is display-only here; checkout happens natively on the PDP.
  // (Credit redemption UI removed 2026-11-02: the /redeem/* routes were
  // disabled by H-079 — the panel showed a permanent "Unavailable" lie.)
  const totalCents = Number(
    shopifyProduct.price_usd_cents ??
      shopifyProduct.price_cents ??
      currentBuyNowProduct?.rawData?.price_usd_cents
  )
  const priceLabel =
    Number.isInteger(totalCents) && totalCents > 0 ? ` · $${(totalCents / 100).toFixed(2)}` : ''
  el.innerHTML = `
    <div class="dshare-buynow-ready">
      <a href="${esc(url)}" target="_blank" rel="noopener noreferrer" class="dshare-btn-buynow" id="sbBuyNowLink">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
        Buy Now${priceLabel}
      </a>
      <button type="button" class="dshare-btn-poster" id="sbPoster">
        <i class="ph ph-image"></i><span>Make a poster</span>
      </button>
    </div>
  `
  bindPosterEntry(shopifyProduct, url)
}

/**
 * H-177 entry A: wire the success-state "Make a poster" button. Opens the
 * server-poster modal with the publish-intent's end_product_id (H-176 D2a
 * contract: publish-intent result carries `end_product_id`) and a PDP share
 * link with the credit token appended. A missing id (backend not yet
 * deployed) or a failed token fetch degrades gracefully — never blocks Buy.
 */
function bindPosterEntry(shopifyProduct, pdpUrl) {
  const btn = document.getElementById('sbPoster')
  if (!btn) return
  btn.onclick = async () => {
    const endProductId = shopifyProduct.end_product_id
    if (!endProductId) {
      showToast('Poster is not available for this product yet', 'warning')
      return
    }
    let shareUrl = pdpUrl
    try {
      const share = await fetchShareToken()
      shareUrl = share?.url || buildShareUrl(pdpUrl, share?.token)
    } catch {
      // Token endpoint pending (H-176 parallel) — the bare PDP link still works.
    }
    openPosterModal({
      endProductId,
      title: currentBuyNowProduct?.rawData?.title || '',
      shareUrl,
    })
  }
}

export function updateBuyNowError(error) {
  const el = document.getElementById('sbBuyNow')
  if (!el) return
  el.innerHTML = `
    <div class="dshare-buynow-error">
      <span>Product creation failed</span>
      <button class="dshare-btn-retry" id="sbBuyNowRetry">Retry</button>
    </div>
  `
  document.getElementById('sbBuyNowRetry').onclick = () => {
    el.innerHTML =
      '<div class="dshare-buynow-loading"><div class="spinner"></div><span>Thinking...</span></div>'
    if (retryFn) retryFn()
  }
}

export function setRetryFn(fn) {
  retryFn = fn
}

export function closeSharePanel() {
  document.getElementById('shareBuyPanel')?.remove()
  currentBuyNowProduct = null
}
